import { t } from "@lingui/core/macro";

import { LoyaltyIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Pressable,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import type { LoyaltyReward } from "../LoyaltyRewardsModal";
import { RewardsNavigationButton } from "./RewardsNavigationButton";

type AvailableRewardsViewProps = {
  rewards: readonly LoyaltyReward[];
  pointTerminologyOne: string;
  pointTerminologyOther: string;
  expiringPointsTotal: number;
  readOnly: boolean;
  onSelectReward?: (reward: LoyaltyReward) => void;
  onViewAllRewards: () => void;
  onViewExpiringPoints: () => void;
};

export const AvailableRewardsView = ({
  rewards,
  pointTerminologyOne,
  pointTerminologyOther,
  expiringPointsTotal,
  readOnly,
  onSelectReward,
  onViewAllRewards,
  onViewExpiringPoints,
}: AvailableRewardsViewProps) => {
  const availableRewards = rewards.filter(
    (reward) => reward.status === "AVAILABLE"
  );
  const expiringPointsTerminology =
    expiringPointsTotal === 1 ? pointTerminologyOne : pointTerminologyOther;

  return (
    <VStack gap={6}>
      <VStack
        gap={4}
        backgroundColor="surface-primary"
        borderColor="surface-dividers"
        borderRadius="4"
        borderWidth={1}
        padding={4}
      >
        <VStack gap={1} testID="loyalty-rewards-available-section">
          <Text variant="mainSmallSemiBold" color="text-secondary">
            {t`AVAILABLE REWARDS`}
          </Text>
          <VStack gap={2}>
            {availableRewards.length > 0 ? (
              availableRewards.map((reward) => (
                <AvailableRewardButton
                  key={reward.id}
                  reward={reward}
                  pointTerminologyOne={pointTerminologyOne}
                  pointTerminologyOther={pointTerminologyOther}
                  readOnly={readOnly}
                  onSelectReward={onSelectReward}
                />
              ))
            ) : (
              <Box
                borderColor="surface-dividers"
                borderRadius="2"
                borderWidth={1}
                backgroundColor="surface-primary"
                padding={4}
              >
                <Text variant="mainSmallSemiBold" color="text-secondary">
                  {t`None`}
                </Text>
              </Box>
            )}
          </VStack>
        </VStack>
        <Box height={1} backgroundColor="surface-dividers" width="100%" />
        <VStack gap={2}>
          <RewardsNavigationButton
            label={t`View All Rewards`}
            count={rewards.length}
            accessibilityLabel={t`View all rewards`}
            testID="loyalty-rewards-view-all-rewards-button"
            onPress={onViewAllRewards}
          />
          {expiringPointsTotal > 0 ? (
            <RewardsNavigationButton
              label={t`${expiringPointsTotal} ${expiringPointsTerminology} Expiring Soon`}
              accessibilityLabel={t`View ${expiringPointsTerminology} expiring soon`}
              testID="loyalty-rewards-view-expiring-points-button"
              onPress={onViewExpiringPoints}
            />
          ) : null}
        </VStack>
      </VStack>
    </VStack>
  );
};

type AvailableRewardButtonProps = {
  reward: LoyaltyReward;
  pointTerminologyOne: string;
  pointTerminologyOther: string;
  readOnly: boolean;
  onSelectReward?: (reward: LoyaltyReward) => void;
};

const AvailableRewardButton = ({
  reward,
  pointTerminologyOne,
  pointTerminologyOther,
  readOnly,
  onSelectReward,
}: AvailableRewardButtonProps) => {
  const terminology =
    reward.points === 1 ? pointTerminologyOne : pointTerminologyOther;
  const isDisabled = readOnly || !onSelectReward;

  return (
    <Pressable
      onPress={() => onSelectReward?.(reward)}
      disabled={isDisabled}
      accessibilityLabel={t`${reward.name}, ${reward.points} ${terminology}`}
      accessibilityRole="button"
      accessibilityState={{ disabled: isDisabled }}
      borderColor="surface-dividers"
      borderRadius="2"
      borderWidth={1}
      backgroundColor="surface-primary"
      overflow="hidden"
      _hover={{ backgroundColor: "surface-secondary" }}
      _pressed={{ backgroundColor: "surface-secondary" }}
      testID={`loyalty-reward-${reward.id}`}
    >
      <HStack alignItems="stretch" width="100%">
        <HStack
          alignItems="center"
          justifyContent="center"
          gap={1}
          width={80}
          backgroundColor="surface-accent-purple-subtler"
        >
          <Text
            variant="mainBaseSemiBold"
            fontWeight="semibold"
            color="text-success-inverse"
          >
            {reward.points}
          </Text>
          <LoyaltyIcon color="icon-primary" width={16} height={16} />
        </HStack>
        <VStack flex={1} justifyContent="center" padding={4}>
          <Text variant="mainSmallSemiBold" color="text-primary">
            {reward.name}
          </Text>
        </VStack>
      </HStack>
    </Pressable>
  );
};
